import type { ZodError } from 'zod';
import type { AxiosError } from 'axios';

interface ErrorConstructor {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  captureStackTrace(targetObject: object, constructorOpt?: new (...args: any[]) => any): void;
}

export class ValidationError extends Error {
  public readonly zodError?: ZodError;
  public readonly data?: unknown;

  constructor(message: string, zodError?: ZodError, data?: unknown) {
    super(message);
    this.name = 'ValidationError';
    this.zodError = zodError;
    this.data = data;
    
    if ('captureStackTrace' in Error) {
      (Error as unknown as ErrorConstructor).captureStackTrace(this, ValidationError);
    }
  }
}

export class ApiError extends Error {
  public readonly status?: number;
  public readonly code?: string;
  public readonly traceId?: string;
  public readonly axiosError?: AxiosError;

  constructor(message: string, axiosError?: AxiosError) {
    const responseData = axiosError?.response?.data as { 
      response_schema?: { response_code?: string; response_message?: string };
      message?: string;
      trace_id?: string;
    } | undefined;
    
    const apiMessage = responseData?.response_schema?.response_message || responseData?.message;
    super(apiMessage || message);
    this.name = 'ApiError';
    this.status = axiosError?.response?.status;
    this.code = responseData?.response_schema?.response_code;
    this.traceId = responseData?.trace_id;
    this.axiosError = axiosError;
    
    if ('captureStackTrace' in Error) {
      (Error as unknown as ErrorConstructor).captureStackTrace(this, ApiError);
    }
  }
}

export class NetworkError extends Error {
  public readonly axiosError?: AxiosError;

  constructor(message: string, axiosError?: AxiosError) {
    super(message);
    this.name = 'NetworkError';
    this.axiosError = axiosError;
    
    if ('captureStackTrace' in Error) {
      (Error as unknown as ErrorConstructor).captureStackTrace(this, NetworkError);
    }
  }
}

export const isValidationError = (error: unknown): error is ValidationError => {
  return error instanceof ValidationError;
};

export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
};

export const isNetworkError = (error: unknown): error is NetworkError => {
  return error instanceof NetworkError;
};
